import React, { useEffect, useState } from 'react'
import { History, Trash2 } from 'lucide-react'
import api from '../api'
import { isAdmin } from '../auth'
import { PageHeader, Card, Table, THead, td, Badge, Button, EmptyState, Pagination, Select, Input, Field } from '../components/ui'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function fmtTanggal(t) { return t ? new Date(t + 'T00:00:00').toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-' }

// Rentang bulan berjalan (WIB) -- dipakai sbg filter default halaman ini (2026-08-05, dulu default-nya
// total SEMUA transaksi sepanjang sejarah yg kurang berguna sbg angka "pendapatan"). Dihitung dari
// timezone Asia/Jakarta eksplisit (bukan timezone browser) supaya konsisten siapapun yg buka halaman ini.
function bulanBerjalanWib() {
  const ym = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta', year: 'numeric', month: '2-digit' }).format(new Date())
  const [tahun, bulan] = ym.split('-').map(Number)
  const hariTerakhir = new Date(tahun, bulan, 0).getDate()
  return { dari: `${ym}-01`, sampai: `${ym}-${String(hariTerakhir).padStart(2, '0')}`, label: new Date(`${ym}-01T00:00:00`).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }) }
}

// History Pembayaran -- read-only: gabungan timeline pembayaran_tagihan (via GET /pembayaran,
// server-side UNION dgn dompet_pelanggan jenis SETOR murni). Mirror pola pagination server-side yg
// sudah dibangun di PelangganKontak.jsx.
//
// Dulu (2026-07-22) py tombol "Terima Pembayaran" bebas-pelanggan (form username+jumlah+tanggal+
// metode+keterangan) -- dihapus 2026-07-28 krn kepanjangan utk kasus yg sering terjadi (bayar duluan
// sebelum tagihan bulan itu digenerate): pelunasan tagihan spesifik sudah py tombolnya sendiri di
// halaman Tagihan (POST /tagihan/:id/pembayaran), dan bayar-duluan/deposit sekarang tombol "Deposit"
// instan (cuma nominal) di Daftar Pelanggan. Halaman ini murni nampilin riwayatnya saja.
export default function HistoryPembayaran() {
  const admin = isAdmin()
  const [data, setData] = useState([])
  const [deletingId, setDeletingId] = useState(null)
  const [total, setTotal] = useState(0)
  const [totalJumlah, setTotalJumlah] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const bulanIni = React.useMemo(bulanBerjalanWib, [])
  const [tanggalDari, setTanggalDari] = useState(bulanIni.dari)
  const [tanggalSampai, setTanggalSampai] = useState(bulanIni.sampai)
  const [dicatatOleh, setDicatatOleh] = useState('all')
  const [dicatatOlehList, setDicatatOlehList] = useState([])

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => { load() }, [page, limit, search, tanggalDari, tanggalSampai, dicatatOleh])
  useEffect(() => { api.get('/pembayaran/dicatat-oleh-list').then(res => setDicatatOlehList(res.data)) }, [])

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/pembayaran', { params: {
        page, limit, search: search || undefined,
        tanggal_dari: tanggalDari || undefined, tanggal_sampai: tanggalSampai || undefined,
        dicatat_oleh: dicatatOleh !== 'all' ? dicatatOleh : undefined
      } })
      setData(res.data.items)
      setTotal(res.data.total)
      setTotalJumlah(res.data.totalJumlah || 0)
    } finally { setLoading(false) }
  }

  // Hapus 1 entri pembayaran yg terkait tagihan (sumber TAGIHAN) -- endpoint sama dgn tombol
  // Batalkan di Tagihan.jsx (DELETE /tagihan/pembayaran/:id, admin-only). Status tagihan TIDAK
  // disimpan sbg kolom (dihitung dari SUM(pembayaran) tiap kali dibaca, lihat hitungStatus() di
  // routes/tagihan.js), jadi begitu baris pembayaran ini hilang, tagihan otomatis balik ke
  // SEBAGIAN/BELUM_LUNAS & muncul lagi di daftar "Belum Lunas" halaman Tagihan -- tanpa perlu
  // sentuh baris tagihan-nya sendiri. Entri sumber DEPOSIT (top-up manual / kelebihan bayar murni,
  // tidak terkait tagihan) SENGAJA tidak dikasih hapus di sini -- beda semantik (koreksi saldo
  // dompet, bukan "batalkan pembayaran tagihan"), di luar cakupan permintaan ini.
  async function hapusPembayaran(item) {
    if (!window.confirm(`Hapus pembayaran ${rp(item.jumlah)} (tagihan periode ${item.periode_terkait}) milik ${item.nama_pelanggan || item.ppp_username}? Tagihan ini akan muncul lagi sebagai belum lunas.`)) return
    setDeletingId(item.id)
    try {
      await api.delete(`/tagihan/pembayaran/${item.id}`)
      load()
    } catch (e) {
      window.alert(e.response?.data?.message || 'Gagal menghapus pembayaran')
    } finally { setDeletingId(null) }
  }

  return (
    <div>
      <PageHeader
        title="History Pembayaran"
        subtitle="Riwayat semua pembayaran & deposit pelanggan -- pelunasan tagihan dicatat dari halaman Tagihan, deposit dari tombol Deposit di Daftar Pelanggan"
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Input value={searchInput} onChange={e => setSearchInput(e.target.value)}
          placeholder="Cari username / nama pelanggan..." style={{ flex: '1 1 220px', minWidth: 220 }} />
        <Field label="Dari" style={{ marginBottom: 0, width: 150 }}>
          <Input type="date" value={tanggalDari} onChange={e => { setPage(1); setTanggalDari(e.target.value) }} />
        </Field>
        <Field label="Sampai" style={{ marginBottom: 0, width: 150 }}>
          <Input type="date" value={tanggalSampai} onChange={e => { setPage(1); setTanggalSampai(e.target.value) }} />
        </Field>
        <Field label="Dikonfirmasi Oleh" style={{ marginBottom: 0, width: 170 }}>
          <Select value={dicatatOleh} onChange={e => { setPage(1); setDicatatOleh(e.target.value) }}>
            <option value="all">Semua</option>
            {dicatatOlehList.map(u => <option key={u} value={u}>{u}</option>)}
          </Select>
        </Field>
      </div>

      <Card style={{ marginBottom: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {tanggalDari === bulanIni.dari && tanggalSampai === bulanIni.sampai && dicatatOleh === 'all'
            ? `Total Pendapatan ${bulanIni.label}`
            : <>
                Total Jumlah Uang{dicatatOleh !== 'all' ? ` (dikonfirmasi oleh ${dicatatOleh})` : ''}
                {(tanggalDari || tanggalSampai) ? ` -- ${tanggalDari || '...'} s/d ${tanggalSampai || '...'}` : ''}
              </>}
          {' '}({total} transaksi)
          {(tanggalDari || tanggalSampai) && (
            <>
              {' -- '}
              <a href="#" onClick={e => { e.preventDefault(); setPage(1); setTanggalDari(''); setTanggalSampai('') }} style={{ color: 'var(--brand)' }}>tampilkan semua</a>
            </>
          )}
        </span>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{loading ? '...' : rp(totalJumlah)}</span>
      </Card>

      <Table>
        <THead cols={['Tanggal', 'Pelanggan', 'Jumlah', 'Metode/Jenis', 'Keterangan', 'Dikonfirmasi Oleh', 'Terkait', '']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={8}><EmptyState icon={History} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={8}><EmptyState icon={History} title="Belum ada riwayat pembayaran" /></td></tr>
          ) : data.map(item => (
            <tr key={`${item.sumber}-${item.id}`}>
              <td style={td}>{fmtTanggal(item.tanggal)}</td>
              <td style={{ ...td, fontWeight: 600 }}>
                {item.nama_pelanggan || item.ppp_username}
                <div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)' }}>{item.ppp_username}</div>
              </td>
              <td style={td}>{rp(item.jumlah)}</td>
              <td style={td}>{item.metode || '-'}</td>
              <td style={td}>{item.keterangan || '-'}</td>
              <td style={td}>{item.dicatat_oleh || '-'}</td>
              <td style={td}>
                {item.periode_terkait
                  ? <Badge tone="brand">Tagihan {item.periode_terkait}</Badge>
                  : <Badge tone="success">Deposit</Badge>}
              </td>
              <td style={td}>
                {admin && item.sumber === 'TAGIHAN' && (
                  <Button size="sm" variant="danger-soft" icon={Trash2} loading={deletingId === item.id} onClick={() => hapusPembayaran(item)}>Hapus</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Card style={{ marginTop: 0, borderTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 16px' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tampilkan</span>
          <Select value={limit} onChange={e => { setPage(1); setLimit(Number(e.target.value)) }} style={{ width: 80 }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
        </div>
        <Pagination page={page} limit={limit} total={total} onChange={setPage} />
      </Card>
    </div>
  )
}
